import { useState, useEffect, useRef, useCallback } from "react";
import {apiFetchDeliveryGuys, assignDeliveryFromAdmin, reAssignDeliveryFromAdmin} from "../service/api/orders";

function StatusBadge({ status }) {
    const online = status === 1;
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "3px 9px", borderRadius: 999,
            fontSize: 11, fontWeight: 600, letterSpacing: "0.03em",
            background: online ? "#ECFDF5" : "#FEF2F2",
            color:      online ? "#059669" : "#EF4444",
            border:    `1px solid ${online ? "#A7F3D044" : "#FCA5A533"}`,
            flexShrink: 0,
        }}>
      <span style={{
          width: 6, height: 6, borderRadius: "50%",
          background: online ? "#10B981" : "#EF4444",
          display: "inline-block",
      }} />
            {online ? "Online" : "Offline"}
    </span>
    );
}

function PartnerRow({ partner, isSelected, onSelect }) {
    const initials = partner.name
        .split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();

    return (
        <div
            className={`dp-row${isSelected ? " is-selected" : ""}`}
            role="option"
            aria-selected={isSelected}
            onClick={() => onSelect(partner)}
        >
            <div className="dp-row-left">
                {partner?.delivery_guy_detail?.photo ? (
                    <div className="dp-avatar">
                        <img src={"https://food.totalfresh.co/assets/img/delivery/" + partner?.delivery_guy_detail?.photo} className="dp-avatar" alt={"nothing"}/>
                    </div>
                ) : (
                    <div className="dp-avatar">
                        {initials}
                    </div>
                )}
                <div className="dp-row-info">
                    <span className="dp-row-name">{partner.name}</span>
                    {partner.email && <span className="dp-row-email">{partner.email}</span>}
                </div>
            </div>
            <StatusBadge status={partner?.delivery_guy_detail.status} />
        </div>
    );
}

const PAGE_LIMIT = 10;

export default function DeliveryPartnerCard({ order, showToast, onOrderUpdate, setPageLoading, zone_id = null }) {
    const [selected, setSelected] = useState(null);
    const [open, setOpen]  = useState(false);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebSrch] = useState("");

    const [partners, setPartners] = useState([]);
    const [page, setPage]         = useState(1);
    const [hasMore, setHasMore]   = useState(true);
    const [total, setTotal]       = useState(null);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState(null);

    const wrapperRef  = useRef(null);
    const searchRef   = useRef(null);
    const sentinelRef = useRef(null);
    const observerRef = useRef(null);


    useEffect(() => {
        const t = setTimeout(() => setDebSrch(search), 350);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        setPartners([]);
        setPage(1);
        setHasMore(true);
        setTotal(null);
        setError(null);
    }, [debouncedSearch]);

    const fetchPage = useCallback(async (pg) => {
        setLoading(true);
        setError(null);
        try {
            const result = await apiFetchDeliveryGuys({
                search:  debouncedSearch,
                page:    pg,
                limit:   PAGE_LIMIT,
                zone_id: zone_id,
            });
            setPartners((prev) => pg === 1 ? result.data : [...prev, ...result.data]);
            setHasMore(result.hasMore);
            setTotal(result.total);
        } catch (e) {
            setError(e.message || "Something went wrong.");
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, zone_id]);

    useEffect(() => {
        if (open) fetchPage(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, debouncedSearch, open]);

    useEffect(() => {
        observerRef.current?.disconnect();

        observerRef.current = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && hasMore && !loading) {
                    setPage((p) => p + 1);
                }
            },
            { threshold: 0.1 }
        );

        if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);
        return () => observerRef.current?.disconnect();
    }, [open, hasMore, loading, partners]);

    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        if (open) setTimeout(() => searchRef.current?.focus(), 60);
        else      setSearch("");
    }, [open]);

    const handleSelect = (partner) => {
        setSelected(partner);
        setOpen(false);
    };

    const handleAssign = async () => {
        if (!selected || selected?.id === order?.accept_delivery?.user?.id) return;

        try {
            setPageLoading("Updating delivery person...");

            let res;
            if (order?.accept_delivery) {
                res = await reAssignDeliveryFromAdmin(order?.id, selected?.id, order?.user?.id);
            } else {
                res = await assignDeliveryFromAdmin(order?.id, selected?.id, order?.user?.id);
            }

            await onOrderUpdate();
            showToast("success", res?.message || "Success");

        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Something went wrong";

            showToast("error", message);
        } finally {
            setPageLoading(false);
        }
    };

    return (
        <>
            <section className="dp-card" ref={wrapperRef}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ minWidth: 0 }}>
                        <div className="dp-label">Delivery Partner</div>

                        <button
                            className={`dp-trigger${open ? " is-open" : ""}`}
                            onClick={() => setOpen((v) => !v)}
                            aria-haspopup="listbox"
                            aria-expanded={open}
                        >
                            <span className={`dp-trigger-text${!selected ? " dp-placeholder" : ""}`}>
                                {selected ? selected.name : "Select partner…"}
                            </span>
                            <svg className={`dp-chevron${open ? " rotated" : ""}`} viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" clipRule="evenodd"
                                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"/>
                            </svg>
                        </button>
                    </div>

                    {selected && <StatusBadge status={selected?.delivery_guy_detail.status} />}
                </div>

                {open && (
                    <div className="dp-panel" role="listbox">

                        {/* Search */}
                        <div className="dp-search-wrap">
                            <svg className="dp-search-icon" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" clipRule="evenodd"
                                      d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"/>
                            </svg>
                            <input
                                ref={searchRef}
                                className="dp-search-input"
                                type="text"
                                placeholder="Search by name…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                aria-label="Search delivery partners"
                            />
                        </div>

                        {/* Result count shown only while searching */}
                        {debouncedSearch && total !== null && (
                            <div className="dp-search-meta">
                                {total} result{total !== 1 ? "s" : ""} for &quot;{debouncedSearch}&quot;
                            </div>
                        )}

                        {/* Scrollable list */}
                        <div className="dp-list">
                            {partners?.map((p) => (
                                <PartnerRow
                                    key={p.id}
                                    partner={p}
                                    isSelected={selected?.id === p.id}
                                    onSelect={handleSelect}
                                />
                            ))}

                            <div ref={sentinelRef} style={{ height: 1 }} />

                            {loading && (
                                <div className="dp-loader">
                                    <span className="dp-loader-dot" />
                                    <span className="dp-loader-dot" />
                                    <span className="dp-loader-dot" />
                                </div>
                            )}

                            {error && !loading && (
                                <div className="dp-state">
                                    {error}
                                    <br />
                                    <button className="dp-retry" onClick={() => fetchPage(page)}>Retry</button>
                                </div>
                            )}

                            {!loading && !error && partners.length === 0 && (
                                <div className="dp-state">
                                    {debouncedSearch
                                        ? `No partners found for "${debouncedSearch}"`
                                        : "No delivery partners available"}
                                </div>
                            )}

                            {!hasMore && !loading && partners.length > 0 && (
                                <div className="dp-end">
                                    All {partners.length} partner{partners.length !== 1 ? "s" : ""} loaded
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {selected && (
                    <button
                        className={`action-btn ${
                            (order?.accept_delivery &&
                                order?.accept_delivery?.user?.id === selected?.id)
                                ? 'disabled'
                                : ''
                        }`}
                        style={{ marginTop: 10 }}
                        disabled={
                            order?.accept_delivery &&
                            order?.accept_delivery?.user?.id === selected?.id
                        }
                        onClick={handleAssign}
                    >
                        {order?.accept_delivery ? (
                            <>
                                {/* Swap icon */}
                                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M8 5a1 1 0 100 2h5.586l-1.293 1.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L13.586 5H8zM12 15a1 1 0 100-2H6.414l1.293-1.293a1 1 0 10-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L6.414 15H12z" />
                                </svg>
                                Reassign Delivery
                            </>
                        ) : (
                            <>
                                {/* Checkmark icon */}
                                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" />
                                </svg>
                                Assign Delivery
                            </>
                        )}
                    </button>
                )}
            </section>
        </>
    );
}